import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfettiBurst } from '@/components/ui/confetti-burst';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

export const POPUP_QUIZ_KEY = 'popup_quiz';

export interface PopupQuizConfig {
  enabled: boolean;
  id: string;
  question: string;
  options: string[];
  correct: number;
  explanation?: string;
}

export function parsePopupQuiz(v: unknown): PopupQuizConfig | null {
  try {
    const o = typeof v === 'string' ? JSON.parse(v) : v;
    if (!o || !o.question || !Array.isArray(o.options)) return null;
    return { enabled: !!o.enabled, id: String(o.id || ''), question: String(o.question),
      options: o.options.map(String), correct: Number(o.correct) || 0, explanation: o.explanation || '' };
  } catch { return null; }
}

const CORRECT_KEY = 'isc-popup-quiz-correct';

/** Quiz ids the student already answered correctly (kept across app opens). */
function answeredCorrectly(id: string): boolean {
  try {
    const arr = JSON.parse(localStorage.getItem(CORRECT_KEY) || '[]');
    return Array.isArray(arr) && arr.includes(id);
  } catch { return false; }
}

function markCorrect(id: string) {
  try {
    const arr = JSON.parse(localStorage.getItem(CORRECT_KEY) || '[]');
    const next = Array.isArray(arr) ? [...arr.filter((x) => x !== id), id].slice(-30) : [id];
    localStorage.setItem(CORRECT_KEY, JSON.stringify(next));
  } catch { /* storage full or blocked — ignore */ }
}

/** Shows the admin's pop-up quiz once per app open (per quiz), and never again once answered correctly. */
export function PopupQuiz() {
  const [quiz, setQuiz] = useState<PopupQuizConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('app_settings').select('value').eq('key', POPUP_QUIZ_KEY).maybeSingle();
      const admin = parsePopupQuiz(data?.value);
      if (admin && !admin.enabled) return; // admin switched the quiz off
      // Auto: one question per day from the latest Current Affairs test.
      const { data: daily } = await (supabase.rpc as any)('get_daily_ca_quiz');
      const q = parsePopupQuiz(daily ? { ...daily, enabled: true } : null) ?? admin;
      if (!q || !q.enabled || q.options.filter(Boolean).length < 2) return;
      if (answeredCorrectly(q.id)) return;
      const seenKey = `isc-popup-quiz-${q.id}`;
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, '1');
      setQuiz(q); setOpen(true);
    })();
  }, []);

  if (!quiz) return null;
  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === quiz.correct) { markCorrect(quiz.id); setBurst((b) => b + 1); }
  };

  return (
    <>
      <ConfettiBurst trigger={burst} theme="flowers" zClass="z-[80]" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">🧠 Quiz of the day</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-medium text-foreground whitespace-pre-line">{quiz.question}</p>
          <div className="space-y-2">
            {quiz.options.map((o, i) => o && (
              <button key={i} type="button" onClick={() => choose(i)} disabled={picked !== null}
                className={cn('w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none',
                  picked === null && 'border-border hover:bg-muted',
                  picked !== null && i === quiz.correct && 'border-success bg-success/15 text-foreground',
                  picked !== null && picked === i && i !== quiz.correct && 'border-destructive bg-destructive/15',
                  picked !== null && picked !== i && i !== quiz.correct && 'border-border opacity-60')}>
                <span className="font-semibold mr-2">{String.fromCharCode(65 + i)}.</span>{o}
              </button>
            ))}
          </div>
          {picked !== null && (
            <div className="text-sm">
              <p className={cn('font-semibold', picked === quiz.correct ? 'text-success' : 'text-destructive')}>
                {picked === quiz.correct ? '🎉 Correct!' : `Wrong — correct answer: ${String.fromCharCode(65 + quiz.correct)}`}
              </p>
              {quiz.explanation && <p className="text-muted-foreground mt-1 whitespace-pre-line">{quiz.explanation}</p>}
            </div>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
